import { Link } from 'react-router-dom';
import { OrdersTable } from './components/orders-table';
import { OrdersCalendar } from './components/orders-calendar';
import { Button as AntButton, Layout } from 'antd';
import HeaderBar from 'Layout/HeaderBar';
import FooterBar from 'Layout/FooterBar';
import { useOrders } from './hooks/use-orders';
import Loader from 'components/Loader';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { cn } from 'lib/utils';
import { Check, MessageCircle, X } from 'lucide-react';
import { OrderStatusEnum, type Order } from './lib/types';
import { OrderDialog } from './components/order-dialog';
import { CreateOrderDialog } from './components/create-order-dialog';
import { Button, buttonVariants } from 'components/ui/button';
import {
  aks2talUsers,
  charlotteUid,
  isAdmin,
  isAdminAndMichael,
  michaelUid,
} from 'components/AdminOnly';
import { useAppSelector } from 'hooks';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Combobox } from 'Views/Machines/components/combobox';
import { OrderManualNextExpectedDeliveryAlerts } from './components/order-manual-delivery-alerts';

const ALL_CREATORS = '__all__';
const ALL_COMPANIES = '__all__';
const ALL_PRODUCTS = '__all__';
const CLAIRITY_PRODUCT_TYPE = {
  id: 'product-type:clairity-plus',
  label: 'CLAIRITY+',
};
const PRODUCT_TYPE_BY_PRODUCT_ID = new Map([
  ['2JEu4U11Zg5ryT5pDL4q', CLAIRITY_PRODUCT_TYPE],
  ['50wpq535YdzqEUMjAXvs', CLAIRITY_PRODUCT_TYPE],
  ['JVMhBRZktrE1SMBzMQsG', CLAIRITY_PRODUCT_TYPE],
]);
const OrdersAssistant = lazy(() => import('./OrdersAssistant'));

export default function Orders() {
  return (
    <Layout>
      <HeaderBar />
      <Layout.Content style={{ margin: '24px 24px 0' }}>
        <OrdersContainer />
      </Layout.Content>
      <FooterBar />
    </Layout>
  );
}

function countOrdersByStatus(
  orders: { status: OrderStatusEnum }[]
): Record<OrderStatusEnum, number> {
  return orders.reduce((acc, order) => {
    const key = order.status;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {} as Record<OrderStatusEnum, number>);
}

export function OrdersContainer() {
  type ViewType = 'table' | 'calendar';

  const currentUserId = useAppSelector((state) => state.user.details.uid);
  const loadedOrgs = useAppSelector((state) => state.orgs.orgs);
  const orgMap = useAppSelector((state) => state.orgs.orgMap);
  const localeCollator = useMemo(
    () =>
      new Intl.Collator('da-DK', {
        numeric: true,
        sensitivity: 'base',
      }),
    []
  );
  const isUserAdmin = isAdmin(currentUserId);
  const isUserMichael = currentUserId === michaelUid;
  const canFilterByCreator = isAdminAndMichael(currentUserId);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [creatorFilter, setCreatorFilter] = useState<string>(
    isUserAdmin ? ALL_CREATORS : currentUserId
  );
  const [companyFilter, setCompanyFilter] = useState<string>(ALL_COMPANIES);
  const [productFilter, setProductFilter] = useState<string>(ALL_PRODUCTS);
  const [viewType, setViewType] = useState<ViewType>('table');
  const [hasAssistantOpened, setHasAssistantOpened] = useState(false);
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);

  const creatorQuery = useMemo(() => {
    if (!canFilterByCreator) {
      return undefined;
    }
    if (isUserAdmin && creatorFilter === ALL_CREATORS) {
      return undefined;
    }
    return creatorFilter;
  }, [canFilterByCreator, creatorFilter, isUserAdmin]);

  const companyQuery = useMemo(
    () => (companyFilter === ALL_COMPANIES ? undefined : companyFilter),
    [companyFilter]
  );

  const { orders, loading } = useOrders({
    creatorId: creatorQuery,
    orgId: companyQuery,
  });
  const accessibleOrgIds = useMemo(() => new Set(loadedOrgs.map((org) => org.id)), [loadedOrgs]);
  const accessScopedOrders = useMemo(
    () => orders.filter((order) => accessibleOrgIds.has(order.orgId)),
    [accessibleOrgIds, orders]
  );
  // Dialog state management (moved from OrdersTable)
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isViewEditDialogOpen, setIsViewEditDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createOrderOrgId, setCreateOrderOrgId] = useState<string | undefined>(undefined);
  const [mode, setMode] = useState<'view' | 'edit'>('view');

  useEffect(() => {
    if (isUserAdmin) {
      setCreatorFilter(ALL_CREATORS);
      return;
    }
    if (isUserMichael) {
      setCreatorFilter(michaelUid);
      return;
    }
    setCreatorFilter(currentUserId);
  }, [currentUserId, isUserAdmin, isUserMichael]);

  const aksUsersNameMap = useMemo(
    () => new Map<string, string>(aks2talUsers.map((user) => [user.id, user.displayName])),
    []
  );

  const creatorOptions = useMemo(() => {
    if (isUserAdmin) {
      const creators = new Map<string, string>();
      orders.forEach((order) => {
        const creatorId = order.createdBy?.id;
        if (!creatorId) {
          return;
        }
        creators.set(
          creatorId,
          order.createdBy?.displayName || aksUsersNameMap.get(creatorId) || creatorId
        );
      });
      return Array.from(creators.entries())
        .map(([id, displayName]) => ({ id, displayName }))
        .sort((a, b) => localeCollator.compare(a.displayName, b.displayName));
    }
    if (isUserMichael) {
      return [michaelUid, charlotteUid].map((id) => ({
        id,
        displayName: aksUsersNameMap.get(id) || id,
      }));
    }
    return [];
  }, [aksUsersNameMap, isUserAdmin, isUserMichael, localeCollator, orders]);

  const companyOptions = useMemo(
    () =>
      loadedOrgs
        .map((org) => ({ id: org.id, label: org.title || org.id }))
        .sort((a, b) => localeCollator.compare(a.label, b.label)),
    [loadedOrgs, localeCollator]
  );

  const productOptions = useMemo(() => {
    const productTypesById = new Map<string, string>();

    orders.forEach((order) => {
      order.products?.forEach((product) => {
        if (!product.id) {
          return;
        }

        const productType = PRODUCT_TYPE_BY_PRODUCT_ID.get(product.id);
        const productTypeId = productType?.id || product.id;
        const productTypeLabel = productType?.label || product.name || product.id;

        productTypesById.set(productTypeId, productTypeLabel);
      });
    });

    return Array.from(productTypesById.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => localeCollator.compare(a.label, b.label));
  }, [localeCollator, orders]);

  useEffect(() => {
    if (companyFilter === ALL_COMPANIES) {
      return;
    }
    if (companyOptions.length === 0) {
      return;
    }
    if (!companyOptions.some((option) => option.id === companyFilter)) {
      setCompanyFilter(ALL_COMPANIES);
    }
  }, [companyFilter, companyOptions]);

  useEffect(() => {
    if (productFilter === ALL_PRODUCTS) {
      return;
    }
    if (!productOptions.some((option) => option.id === productFilter)) {
      setProductFilter(ALL_PRODUCTS);
    }
  }, [productFilter, productOptions]);

  const toggleFilter = (filterId: string) => {
    setSelectedFilters((prev) =>
      prev.includes(filterId) ? prev.filter((id) => id !== filterId) : [...prev, filterId]
    );
  };

  // Shared handlers for both table and calendar
  const handleCreateOrder = (orgId?: string) => {
    setCreateOrderOrgId(orgId);
    setIsCreateDialogOpen(true);
  };

  const handleCreateDialogOpenChange = (open: boolean) => {
    setIsCreateDialogOpen(open);
    if (!open) {
      setCreateOrderOrgId(undefined);
    }
  };

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    setMode('view');
    setIsViewEditDialogOpen(true);
  };

  const handleEditOrder = (order: Order) => {
    setSelectedOrder(order);
    setMode('edit');
    setIsViewEditDialogOpen(true);
  };

  const handleToggleAssistant = () => {
    if (!isAssistantOpen) {
      setHasAssistantOpened(true);
    }
    setIsAssistantOpen((current) => !current);
  };

  const statusCounts = countOrdersByStatus(orders);

  const filteredOrders = orders.filter((order) => {
    const matchesStatus = selectedFilters.length === 0 || selectedFilters.includes(order.status);
    const matchesProduct =
      productFilter === ALL_PRODUCTS ||
      order.products?.some((product) => {
        const productTypeId = PRODUCT_TYPE_BY_PRODUCT_ID.get(product.id)?.id || product.id;
        return productTypeId === productFilter;
      });

    return matchesStatus && matchesProduct;
  });

  const creatorSelectControl = canFilterByCreator ? (
    <div className="w-[220px]">
      <Select value={creatorFilter} onValueChange={setCreatorFilter}>
        <SelectTrigger>
          <SelectValue placeholder="Oprettet af" />
        </SelectTrigger>
        <SelectContent>
          {isUserAdmin && <SelectItem value={ALL_CREATORS}>Alle</SelectItem>}
          {creatorOptions.map((option) => (
            <SelectItem key={option.id} value={option.id}>
              {option.displayName}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  ) : null;

  const companyFilterOptions = useMemo(
    () => [
      { value: ALL_COMPANIES, label: 'Alle firmaer' },
      ...companyOptions.map((option) => ({ value: option.id, label: option.label })),
    ],
    [companyOptions]
  );

  const companySelectControl = (
    <div className="w-[220px]">
      <Combobox
        options={companyFilterOptions}
        value={companyFilter}
        onChange={(value) => setCompanyFilter(value || ALL_COMPANIES)}
        placeholder="Firma"
        searchPlaceholder="Søg firma..."
        emptyMessage="Ingen firmaer fundet."
        popoverClassName="w-[420px]"
      />
    </div>
  );

  const productFilterOptions = useMemo(
    () => [
      { value: ALL_PRODUCTS, label: 'Alle produkter' },
      ...productOptions.map((option) => ({ value: option.id, label: option.label })),
    ],
    [productOptions]
  );

  const productSelectControl = (
    <div className="w-[220px]">
      <Combobox
        options={productFilterOptions}
        value={productFilter}
        onChange={(value) => setProductFilter(value || ALL_PRODUCTS)}
        placeholder="Produkt"
        searchPlaceholder="Søg produkt..."
        emptyMessage="Ingen produkter fundet."
        popoverClassName="w-[420px]"
      />
    </div>
  );

  const assistantContext = useMemo(
    () => ({
      selectedCompanyId: companyQuery || null,
      selectedCompanyName:
        companyFilterOptions.find((option) => option.value === companyFilter)?.label || null,
      creatorId: creatorQuery || null,
      creatorName:
        creatorOptions.find((option) => option.id === creatorFilter)?.displayName || null,
      selectedStatuses: selectedFilters,
    }),
    [
      companyFilter,
      companyFilterOptions,
      companyQuery,
      creatorFilter,
      creatorOptions,
      creatorQuery,
      selectedFilters,
    ]
  );

  if (loading) {
    return (
      <div className="flex flex-col h-full justify-center items-center gap-2">
        <Loader size={48} />
        <p className="text-lg ml-4">Loader ordre...</p>
      </div>
    );
  }

  return (
    <>
      <OrderManualNextExpectedDeliveryAlerts
        orders={accessScopedOrders}
        orgMap={orgMap}
        onSelectOrder={handleEditOrder}
      />

      <div className="flex flex-wrap gap-2 justify-between mb-4">
        <div className="flex flex-wrap gap-2">
          {filterOptions.map((option) => {
            const isSelected = selectedFilters.includes(option.id);

            return (
              <button
                key={option.id}
                onClick={() => toggleFilter(option.id)}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm rounded-md border transition-all duration-200 hover:shadow-sm',
                  isSelected
                    ? 'bg-gray-100 border-gray-300 shadow-sm'
                    : 'bg-white border-gray-200 hover:bg-gray-50'
                )}
              >
                <div className={cn('w-2 h-2 rounded-full', option.color)} />
                <span className="text-gray-700 whitespace-nowrap">{option.label}</span>
                <span className="text-gray-700 whitespace-nowrap font-bold">
                  ({statusCounts[option.id] || 0})
                </span>
                {isSelected && <Check className="w-4 h-4 text-gray-600" />}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap justify-end items-center gap-2">
          {viewType === 'calendar' && companySelectControl}
          {viewType === 'calendar' && productSelectControl}
          {viewType === 'calendar' && creatorSelectControl}
          <Button
            variant={viewType === 'table' ? 'default' : 'outline'}
            aria-pressed={viewType === 'table'}
            className="rounded-tr-none rounded-br-none"
            onClick={() => setViewType('table')}
          >
            Liste
          </Button>
          <Button
            variant={viewType === 'calendar' ? 'default' : 'outline'}
            aria-pressed={viewType === 'calendar'}
            className="rounded-tl-none rounded-bl-none border-l-0"
            onClick={() => setViewType('calendar')}
          >
            Kalender
          </Button>
          <Link to="/orders/prognosis" className={buttonVariants({ variant: 'outline' })}>
            Kundeopfølgning
          </Link>
        </div>
      </div>

      {viewType === 'table' && (
        <OrdersTable
          orders={filteredOrders}
          onViewOrder={handleViewOrder}
          onEditOrder={handleEditOrder}
          onCreateOrder={() => handleCreateOrder()}
          headerControls={
            <div className="flex items-center gap-2">
              {companySelectControl}
              {productSelectControl}
              {creatorSelectControl}
            </div>
          }
        />
      )}
      {viewType === 'calendar' && (
        <OrdersCalendar
          orders={filteredOrders}
          onOrderClick={(orderId) => {
            const order = filteredOrders.find((o) => o.id === orderId);
            if (order) handleViewOrder(order);
          }}
        />
      )}
      <div
        style={{
          border: '1px solid #e7ecf3',
          borderRadius: 8,
          bottom: 96,
          boxShadow: '0 18px 48px rgba(15, 23, 42, 0.18)',
          display: isAssistantOpen ? 'block' : 'none',
          height: 'min(720px, calc(100vh - 128px))',
          overflow: 'hidden',
          position: 'fixed',
          right: 24,
          width: 'min(640px, calc(100vw - 32px))',
          zIndex: 1000,
        }}
      >
        {hasAssistantOpened ? (
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center bg-white">
                <Loader size={40} />
              </div>
            }
          >
            <OrdersAssistant
              compact
              context={assistantContext}
              height="100%"
              messageListHeight="none"
            />
          </Suspense>
        ) : null}
      </div>
      <AntButton
        aria-label={isAssistantOpen ? 'Luk ordre AI' : 'Åbn ordre AI'}
        icon={isAssistantOpen ? <X size={20} /> : <MessageCircle size={20} />}
        shape="circle"
        size="large"
        style={{
          bottom: 24,
          boxShadow: '0 12px 32px rgba(20, 85, 166, 0.28)',
          height: 56,
          position: 'fixed',
          right: 24,
          width: 56,
          zIndex: 1001,
        }}
        type="primary"
        onClick={handleToggleAssistant}
      />

      {/* Shared dialogs */}
      <CreateOrderDialog
        open={isCreateDialogOpen}
        onOpenChange={handleCreateDialogOpenChange}
        orgId={createOrderOrgId}
      />

      <OrderDialog
        open={isViewEditDialogOpen}
        onOpenChange={setIsViewEditDialogOpen}
        order={selectedOrder}
        mode={mode}
      />
    </>
  );
}
// function OrderListContainer(props: any) {
//   useEffect(() => {
//     // props.fetchOrgs(props.uid);
//     props.fetchOrders();

//     return () => {};
//   }, []);

//   return (
//     <OrderList
//       {...props}
//       loading={props.loading}
//       addOrder={({ initials, amount, productId }: any) =>
//         props.addOrder({ initials, amount, productId })
//       }
//       updateOrder={({ orderId, data }: any) => props.updateOrder({ orderId, data })}
//     />
//   );
// }

// function mapOrders(orders: any, orgs: any) {
//   return orders.map((order: any) => ({
//     ...order,
//     org: orgs[order.orgId] && orgs[order.orgId].title,
//   }));
// }

// function mapStateToProps(state: AppState) {
//   return {
//     orders: mapOrders(state.orders.list, state.orgs.orgMap),
//     loading: state.orders.loading,
//     uid: state.user.details.uid,
//   };
// }

// const mapActionsToProps = {
//   fetchOrgs,
//   fetchOrders,
//   updateOrder,
//   addOrder,
//   deleteOrder,
// };

// const withConnect = connect(mapStateToProps, mapActionsToProps);

// export default compose(withConnect, withRouter)(OrderListContainer);

interface FilterOption {
  id: OrderStatusEnum;
  label: string;
  color: string;
}

const filterOptions: FilterOption[] = [
  {
    id: OrderStatusEnum.AWAITING,
    label: 'Afventer godkendelse',
    color: 'bg-orange-400',
  },
  {
    id: OrderStatusEnum.AWAITING_DISTRIBUTION,
    label: 'Afventer distributionslager',
    color: 'bg-orange-400',
  },
  {
    id: OrderStatusEnum.PROCESSING,
    label: 'Behandles',
    color: 'bg-blue-500',
  },
  {
    id: OrderStatusEnum.READY_FOR_SHIPPING,
    label: 'Klar til afsendelse',
    color: 'bg-purple-500',
  },
  {
    id: OrderStatusEnum.IN_TRANSIT,
    label: 'Under forsendelse',
    color: 'bg-purple-500',
  },
  {
    id: OrderStatusEnum.AWAITING_INVOICE,
    label: 'Afventer fakturering',
    color: 'bg-cyan-500',
  },
  {
    id: OrderStatusEnum.COMPLETED,
    label: 'Gennemført',
    color: 'bg-green-500',
  },
  {
    id: OrderStatusEnum.VOIDED,
    label: 'Annulleret',
    color: 'bg-red-500',
  },
];
